import { interval, timer } from 'rxjs'
import { filter, first, switchMap, takeUntil } from 'rxjs/operators'

import { Injector } from '../../injector'
import { _groupBy, _sort } from '../../script-utils'
import { CashierService } from '../../services/CashierService'
import { PurchasePackageService } from '../../services/PurchasePackages/PurchasePackageService'
import {
	AvatarItem,
	getAvatarFromPackageContents,
	getGoldCoinsFromPackageContents,
	getJsngEntriesFromPackageContents,
	getSweepsCoinsFromPackageContents,
	getTournamentEntriesFromPackageContents,
	JsngEntryItem,
	Package,
	PackageContentType,
	TournamentEntryItem,
} from '../../services/PurchasePackages/types'
import { PaymentProviders } from '../../ui/cashier/PaymentProvider'
import { triggerGTMPurchase } from '../../ui/cashier/util/GoogleTagManager'
import { triggerImpactsConversionTracking } from '../../ui/cashier/util/ImpactsConversionTracking'
import { PaymentProcessingState } from '../Reducers'
import { preventDuplicateRequest } from '../ReduxUtils'
import { getStore } from '../Store'
import { PlayerProfileService } from '../../services/PlayerProfileService'
import { PlayerItemView } from '../../communication/PlayerApiProtocol.generated'
import { Utils } from '../../Utils'

export const enum PaymentProcessingStatus {
    PENDING = 'PENDING',
    FAILED = 'FAILED',
    SUCCESS = 'SUCCESS',
    CANCELLED = 'CANCELLED',
    SLOW = 'SLOW',
}

export enum PurchaseActionType {
    SELECT_BUNDLE = 'SELECT_PURCHASE_BUNDLE',
    RESET_PURCHASE = 'RESET_PURCHASE_PROCESS',
    SELECT_PROVIDER = 'SELECT_PURCHASE_PROVIDER',
    RESET_PROVIDER = 'RESET_PURCHASE_PROVIDER',
    SUBMIT_PURCHASE = 'SUBMIT_PURCHASE_PURCHASE',
    RECEIVE_PURCHASE_RESPONSE = 'RECEIVE_PURCHASE_RESPONSE',
    SET_SESSION_ID = 'SET_SESSION_ID',
    SET_PROCESSING_STATUS = 'SET_PAYMENT_PROCESSING_STATUS',
    FETCH_PURCHASE_LIMITS = 'FETCH_PURCHASE_LIMITS',
    RECEIVE_PURCHASE_LIMITS = 'RECEIVE_PURCHASE_LIMITS',
    FETCH_BUNDLES = 'FETCH_PURCHASE_BUNDLES',
    FETCH_BUNDLE_ITEMS = 'FETCH_BUNDLE_ITEMS',
    RECEIVE_BUNDLE_ITEMS = 'RECEIVE_BUNDLE_ITEMS',
    RECEIVE_BUNDLES = 'RECEIVE_PURCHASE_BUNDLES',
    RECEIVE_PAYMENTS_URL = 'RECEIVE_PAYMENTS_URL',
    SET_BONUS_CODE = 'SET_BONUS_CODE',
    HAS_MADE_PURCHASES = 'HAS_MADE_PURCHASES',
    SET_PURCHASE_SOURCE = 'SET_PURCHASE_SOURCE',
}

interface SetPurchaseBundle {
    type: typeof PurchaseActionType.SELECT_BUNDLE;
    payload: Bundle;
}

interface ResetPurchaseBundle {
    type: typeof PurchaseActionType.RESET_PURCHASE;
    payload?: undefined;
}

interface SetPurchaseProvider {
    type: typeof PurchaseActionType.SELECT_PROVIDER;
    payload: { provider: PaymentProvider; token?: string };
}

interface ResetPurchaseProvider {
    type: typeof PurchaseActionType.RESET_PROVIDER;
}

interface SubmitPurchase {
    type: typeof PurchaseActionType.SUBMIT_PURCHASE;
    payload?: undefined;
}

interface ReceivePurchaseResponse {
    type: typeof PurchaseActionType.RECEIVE_PURCHASE_RESPONSE;
    payload: { response?: PreparePaymentResponse; error?: string };
}

interface SetSessionId {
    type: typeof PurchaseActionType.SET_SESSION_ID;
    payload: string;
}

interface SetProcessingStatus {
    type: typeof PurchaseActionType.SET_PROCESSING_STATUS;
    payload: PaymentProcessingState;
}

interface FetchPurchaseLimits {
    type: typeof PurchaseActionType.FETCH_PURCHASE_LIMITS;
    payload?: undefined;
}

interface ReceivePurchaseLimits {
    type: typeof PurchaseActionType.RECEIVE_PURCHASE_LIMITS;
    payload: PurchaseLimitInfo;
}

interface FetchBundles {
    type: typeof PurchaseActionType.FETCH_BUNDLES;
    payload?: undefined;
}

interface ReceiveBundles {
    type: typeof PurchaseActionType.RECEIVE_BUNDLES;
    payload: {
        bundles: Bundle[];
        offers: Bundle[];
        bonus: Bundle[];
    };
}

interface ReceivePaymentsUrl {
    type: typeof PurchaseActionType.RECEIVE_PAYMENTS_URL;
    payload: { url: string };
}

interface SetBonusCode {
    type: typeof PurchaseActionType.SET_BONUS_CODE;
    payload: string;
}

interface FetchBundleItems {
    type: typeof PurchaseActionType.FETCH_BUNDLE_ITEMS;
    payload?: undefined;
}

interface ReceiveBundleItems {
    type: typeof PurchaseActionType.RECEIVE_BUNDLE_ITEMS;
    payload: {
        items: PlayerItemView[],
    }

}

interface HasMadePurchases {
    type: typeof PurchaseActionType.HAS_MADE_PURCHASES;
    payload: boolean;
}

interface SetPurchaseSource {
    type: typeof PurchaseActionType.SET_PURCHASE_SOURCE;
    payload: string;
}

export type PurchaseAction =
    | SetPurchaseBundle
    | ResetPurchaseBundle
    | SetPurchaseProvider
    | ResetPurchaseProvider
    | SubmitPurchase
    | ReceivePurchaseResponse
    | SetSessionId
    | SetProcessingStatus
    | FetchPurchaseLimits
    | ReceivePurchaseLimits
    | FetchBundles
    | ReceiveBundles
    | ReceivePaymentsUrl
    | SetBonusCode
    | FetchBundleItems
    | ReceiveBundleItems
    | HasMadePurchases
    | SetPurchaseSource;

export function dispatchPurchaseAction(action: PurchaseAction) {
	getStore().dispatch(action)
}

export function checkHasCustomerMadePurchases(): Promise<void> {
	return Injector.get(CashierService)
		.hasUserMadePurchases()
		.then((hasUserMadePurchases) => {
			dispatchPurchaseAction({type: PurchaseActionType.HAS_MADE_PURCHASES, payload: hasUserMadePurchases})
		})
}

export function preparePurchaseState(): Promise<void> {
	return Promise.all([
		fetchPurchaseLimits(),
		fetchBundles()
	]
	).then(() => {})
}

// Action helpers below

export function setSelectedBundle(bundle: Bundle): void {
	dispatchPurchaseAction({ type: PurchaseActionType.SELECT_BUNDLE, payload: bundle })
}

export function setPurchaseProvider(provider: PaymentProvider, token?: string): void {
	dispatchPurchaseAction({ type: PurchaseActionType.SELECT_PROVIDER, payload: { provider, token } })
}

export function setPurchaseSource(source: string): void {
	dispatchPurchaseAction({ type: PurchaseActionType.SET_PURCHASE_SOURCE, payload: source })
}

export const readyToSubmitPurchase = () => {
	const state = getStore().getState()
	const user = state.user
	return user && user.userInfo.kycInfoUpdated && !state.phoneVerification.phoneVerificationRequired
}

export const submitPurchaseRequest = (): void => {
	dispatchPurchaseAction({ type: PurchaseActionType.SUBMIT_PURCHASE })
	preparePayment()
}
function preparePayment(): void {
	const { bundle, provider, providerToken, sessionId, source } = getStore().getState().purchase
	Injector.get(CashierService)
		.preparePayment(bundle, provider, providerToken, sessionId, source)
		.then(handlePreparePaymentResponse)
		.catch(handlePreparePaymentError)
}

const handlePreparePaymentResponse = (response: PreparePaymentResponse): void => {
	dispatchPurchaseAction({
		type: PurchaseActionType.RECEIVE_PAYMENTS_URL,
		payload: { url: response.forwardURL },
	})
}

const handlePreparePaymentError = (): void => {
	dispatchPurchaseAction({
		type: PurchaseActionType.RECEIVE_PURCHASE_RESPONSE,
		payload: { error: i18n.t('cashier.generic.error') },
	})
}

export function resetPurchaseState(): void {
	dispatchPurchaseAction({ type: PurchaseActionType.RESET_PURCHASE })
}

export function resetPurchaseProvider(): void {
	dispatchPurchaseAction({ type: PurchaseActionType.RESET_PROVIDER })
}

export function setSessionId(sessionId: string): void {
	dispatchPurchaseAction({ type: PurchaseActionType.SET_SESSION_ID, payload: sessionId })
}

export function setPaymentProcessingStatus(payload: PaymentProcessingState): void {
	if (payload.status.toUpperCase() === PaymentProcessingStatus.SUCCESS) {
		const state = getStore().getState()
		const price = state.purchase.bundle.price
		const userId = state.user.userInfo.userId
		const userEmail = state.user.userInfo.userEmail
		const bundleId = state.purchase.bundle.id
		triggerGTMPurchase(userId, price, userEmail, payload.invoiceId, payload.firstPurchase)
		if (payload.firstPurchase) triggerImpactsConversionTracking()
	}
	dispatchPurchaseAction({ type: PurchaseActionType.SET_PROCESSING_STATUS, payload })
}

export function setPurchaseLimits(purchaseLimitInfo: PurchaseLimitInfo): void {
	dispatchPurchaseAction({ type: PurchaseActionType.RECEIVE_PURCHASE_LIMITS, payload: purchaseLimitInfo })
}

export function setPurchaseBundles(bundles: Bundle[] = [], offers: Bundle[] = [], bonus: Bundle[] = []): void {
	dispatchPurchaseAction({ type: PurchaseActionType.RECEIVE_BUNDLES, payload: { bundles, offers, bonus } })
}

export const fetchPurchaseLimits = (): void => {
	dispatchPurchaseAction({ type: PurchaseActionType.FETCH_PURCHASE_LIMITS })
	Injector.get(CashierService)
		.getUserPurchaseLimits()
		.then(setPurchaseLimits)
}

export const fetchBundles = preventDuplicateRequest(() => {
	return fetchAndSetBundlesFromOldAndNewSystem().then(bundles => {
		const itemNameIds = Utils.flatMap(x => x.contents, bundles.bonus.concat(bundles.regular).concat(bundles.special))
			.filter(b => b.type === PackageContentType.AVATAR || b.type === PackageContentType.JSNG_ENTRY || b.type === PackageContentType.TOURNAMENT_ENTRY)
			.map((a: JsngEntryItem | AvatarItem | TournamentEntryItem) => a.nameId)
			.filter((value, index, self) => {
				return self.indexOf(value) === index // Unique strings
			})
		if (itemNameIds.length > 0) {
			fetchBundleItems(itemNameIds)
		}
		return bundles
	})
})

const fetchAndSetBundlesFromOldAndNewSystem = () => {
	dispatchPurchaseAction({ type: PurchaseActionType.FETCH_BUNDLES })
	return PurchasePackageService.getPackages().then(packages => {
		const { regular = [], special = [], bonus = [] } = _groupBy(_sort(packages, 'price', true), p =>
			p.isBonus ? 'bonus' : p.isGloballyAssigned ? 'regular' : 'special',
		)
		return {regular, special, bonus}
	}).then(({regular, special, bonus}) => {
		setPurchaseBundles(regular.map(packageToBundle), special.map(packageToBundle), bonus.map(packageToBundle))
		return {regular, special, bonus}
	})
}

export function setBundleItems(items: PlayerItemView[] ): void {
	dispatchPurchaseAction({ type: PurchaseActionType.RECEIVE_BUNDLE_ITEMS, payload: { items } })
}

export const fetchBundleItems = (itemNameIds: string[]) => fetchAndSetItemsInBundles(itemNameIds)

const fetchAndSetItemsInBundles = (itemNameIds: string[]) => {
	dispatchPurchaseAction({ type: PurchaseActionType.FETCH_BUNDLE_ITEMS })
	return PlayerProfileService.getItemsByNameIds(itemNameIds).then(items => {
		setBundleItems(items)
		return items
	}).catch(err => {
		setBundleItems([])
		return err
	})
}

export function packageToBundle(pkg: Package): Bundle {
	const sweepsCoins = getSweepsCoinsFromPackageContents(pkg.contents)
	const goldCoins = getGoldCoinsFromPackageContents(pkg.contents)
	const avatar = getAvatarFromPackageContents(pkg.contents)
	const jsngEntries = getJsngEntriesFromPackageContents(pkg.contents)
	const tournamentEntries = getTournamentEntriesFromPackageContents(pkg.contents)

	return {
		id: Number(pkg.packageId),
		targetSite: pkg.platform,
		playType: pkg.playType,
		chips: goldCoins.amount,
		sweeps: sweepsCoins ? sweepsCoins.amount.toFixed(2) : '0',
		tagline: pkg.name,
		price: pkg.price.toFixed(2),
		referencePrice: pkg.referencePrice?.toFixed(2),
		expiryTimestamp: pkg.expiresAt && pkg.expiresAt.seconds,
		claimLimit: pkg.claimLimit,
		avatar: avatar?.nameId,
		jsngEntries,
		tournamentEntries,
	}
}

export const watchPendingPurchases = () => {
	const { invoiceId, status, provider } = getStore().getState().purchase.processingStatus
	const cashierService = Injector.get(CashierService)

	// Nothing to watch for
	if (purchaseStatusHasUpdated(status, provider)) return () => {}

	const statusChange$ = interval(3000).pipe(
		// Fetch the purchase info
		switchMap(() => cashierService.getPurchaseInfo(invoiceId)),
		filter(res => purchaseStatusHasUpdated(res.status, res.provider)),
		// We only need the first result that is ready
		first(),
	)
	// Repeat every 5 seconds
	const subscription1 = statusChange$.subscribe(result => {
		setPaymentProcessingStatus({
			status: result.status as any,
			error: result.error,
			invoiceId,
			firstPurchase: result.firstPurchase,
			provider: result.provider,
		})
	})
	// Mark the status as 'slow' after 10 seconds without an update.
	const subscription2 = timer(10000)
		.pipe(takeUntil(statusChange$))
		.subscribe(slowRequest => {
			setPaymentProcessingStatus({
				status: PaymentProcessingStatus.SLOW,
				invoiceId,
			})
		})

	return () => {
		subscription1.unsubscribe()
		subscription2.unsubscribe()
	}
}

function handleExternalPurchase(paymentWindow: Window) {
	window.addEventListener('message', onWindowMessage)

	// Poll for progress updates and also check for the window being prematurely closed.
	const progressPoll = setInterval(() => {
		if (paymentWindow.closed) {
			setPaymentProcessingStatus({ status: PaymentProcessingStatus.CANCELLED, invoiceId: '', error: '' })
			window.removeEventListener('message', onWindowMessage)
			clearInterval(progressPoll)
		} else {
			// Non sensitive info - we poll the window with this message. Our redirect page (after purchase complete)
			// will listen for this message and then respond with the final status.
			// Hopefully the intermediate pages (worldpay / skrill) will ignore / handle this message.
			paymentWindow.postMessage({ type: 'CHECK_PROGRESS' }, '*')
		}
	}, 200)

	function onWindowMessage(message: MessageEvent) {
		// SECURITY: Ignore messages that are not coming from the expected window.
		if (message.source !== paymentWindow) return
		// SECURITY: Ignore messages that are not coming from our return page.
		if (message.origin !== window.location.origin) return
		// Ignore messages of a type we did not expect.
		if (!message.data || typeof message.data !== 'object' || message.data.type !== 'PAYMENT_PROGRESS') return

		setPaymentProcessingStatus(message.data)

		window.removeEventListener('message', onWindowMessage)
		clearInterval(progressPoll)
		paymentWindow.close()
	}
}

function purchaseStatusHasUpdated(status: string, provider: string) {
	// We only care if the status is NOT pending or if it is Flykk/ACH
	// For Flykk, we care because the user may defer the purchase leaving it in a pending status
	return (
		(status !== PaymentProcessingStatus.PENDING && status !== PaymentProcessingStatus.SLOW) ||
        (provider && provider.toLowerCase() === PaymentProviders.FLYKK.toLowerCase()) ||
        (provider && provider.toLowerCase() === PaymentProviders.ACH.toLowerCase())
	)
}
